import React from "react"


import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useAuth } from "./useAuth"

type Booking = {
  id: string
  area_id: string
  user_id: string
  booking_date: string
  start_time: string
  end_time: string
  status: 'pending' | 'confirmed' | 'cancelled' | 'rejected'
  notes?: string
  approved_by?: string
  approved_at?: string
  created_at: string
  updated_at: string
  condominium_id: string
  common_areas?: {
    name: string
    image_emoji: string
  }
  profiles?: {
    full_name: string
  }
}

type CreateBookingData = {
  area_id: string
  booking_date: string
  start_time: string
  end_time: string
  notes?: string
}

export const useMyBookings = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  try {
    const queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  return useQuery({
    queryKey: ["my-bookings", profile?.id],
    queryFn: async () => {
      if (!profile?.id) {
        console.log("Profile não encontrado para buscar agendamentos")
        return []
      }

      console.log("Buscando agendamentos para usuário:", profile.id)

      const { data, error } = await supabase
        .from("bookings")
        .select(`
          *,
          common_areas!bookings_area_id_fkey(name, image_emoji)
        `)
        .eq("user_id", profile.id)
        .order("booking_date", { ascending: true })

      if (error) {
        console.error("Erro ao buscar agendamentos:", error)
        toast.error("Erro ao carregar seus agendamentos")
        throw error
      }
      
      console.log("Agendamentos encontrados para o usuário:", data?.length || 0)
      console.log("Detalhes dos agendamentos:", data)
      
      // Filtrar agendamentos que não venceram (considerando data + hora de fim)
      const now = new Date()
      // Usar formato de data local para comparação precisa
      const currentDate = now.toLocaleDateString('en-CA') // formato YYYY-MM-DD
      const currentTime = now.toTimeString().split(' ')[0]
      
      const activeBookings = (data || []).filter(booking => {
        const bookingDate = booking.booking_date
        const endTime = booking.end_time
        
        console.log(`Comparando agendamento: ${bookingDate} vs data atual: ${currentDate}`)
        
        // Se a data é futura, manter
        if (bookingDate > currentDate) return true
        
        // Se a data é hoje, verificar se o horário de fim não passou
        if (bookingDate === currentDate) {
          console.log(`Agendamento é hoje, comparando horários: ${endTime} vs ${currentTime}`)
          return endTime > currentTime
        }
        
        // Se a data é passada, remover
        return false
      })
      
      return activeBookings.map(booking => ({
        ...booking,
        area_name: booking.common_areas?.name || 'Área desconhecida'
      })) as (Booking & { area_name: string })[]
    },
    enabled: !!profile?.id
  })
}

export const useAllBookings = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  try {
    const queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  return useQuery({
    queryKey: ["all-bookings", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) {
        console.log("Condominium ID não encontrado para buscar todos os agendamentos")
        return []
      }

      console.log("Buscando todos os agendamentos para condomínio:", profile.condominium_id)

      const { data, error } = await supabase
        .from("bookings")
        .select(`
          *,
          common_areas!bookings_area_id_fkey(name, image_emoji),
          profiles!bookings_user_id_fkey(full_name)
        `)
        .eq("condominium_id", profile.condominium_id)
        .order("booking_date", { ascending: true })

      if (error) {
        console.error("Erro ao buscar todos os agendamentos:", error)
        toast.error("Erro ao carregar agendamentos")
        throw error
      }
      
      console.log("Todos os agendamentos encontrados:", data?.length || 0)
      console.log("Detalhes de todos os agendamentos:", data)
      
      // Filtrar agendamentos que não venceram (considerando data + hora de fim)
      const now = new Date()
      const currentDate = now.toISOString().split('T')[0]
      const currentTime = now.toTimeString().split(' ')[0]
      
      const activeBookings = (data || []).filter(booking => {
        const bookingDate = booking.booking_date
        const endTime = booking.end_time
        
        // Se a data é futura, manter
        if (bookingDate > currentDate) return true
        
        // Se a data é hoje, verificar se o horário de fim não passou
        if (bookingDate === currentDate) {
          return endTime > currentTime
        }
        
        // Se a data é passada, remover
        return false
      })
      
      return activeBookings.map(booking => ({
        ...booking,
        area_name: booking.common_areas?.name || 'Área desconhecida',
        user_name: booking.profiles?.full_name || 'Usuário desconhecido'
      })) as (Booking & { area_name: string; user_name: string })[]
    },
    enabled: !!profile?.condominium_id && (profile?.role === 'sindico' || profile?.role === 'administracao')
  })
}

export const useCreateBooking = () => {
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return a mock mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: () => Promise.resolve(),
      isLoading: false,
      isPending: false,
      error: null
    }
  }
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: CreateBookingData) => {
      if (!profile?.id || !profile?.condominium_id) throw new Error("Usuário não autenticado")

      console.log("Criando agendamento:", data)

      // Verificar conflitos de horário
      const { data: conflicts, error: conflictError } = await supabase
        .from("bookings")
        .select("id, start_time, end_time, status")
        .eq("area_id", data.area_id)
        .eq("booking_date", data.booking_date)
        .in("status", ["pending", "confirmed"])
      
      if (conflictError) {
        console.error("Erro ao verificar conflitos:", conflictError)
        throw conflictError
      }
      
      console.log("Agendamentos existentes para verificação:", conflicts)
      console.log("Novo agendamento:", { start_time: data.start_time, end_time: data.end_time })
      
      // Verificar sobreposição de horários manualmente
      const hasConflict = conflicts?.some(existing => {
        const existingStart = existing.start_time
        const existingEnd = existing.end_time
        const newStart = data.start_time
        const newEnd = data.end_time
        
        // Verifica se há sobreposição: novo horário começa antes do existente terminar E termina depois do existente começar
        const overlap = newStart < existingEnd && newEnd > existingStart
        console.log(`Verificando conflito: ${existingStart}-${existingEnd} vs ${newStart}-${newEnd} = ${overlap}`)
        return overlap
      })
      
      if (hasConflict) {
        throw new Error("Já existe um agendamento neste horário")
      }

      const { data: booking, error } = await supabase
        .from("bookings")
        .insert({
          area_id: data.area_id,
          user_id: profile.id,
          booking_date: data.booking_date,
          start_time: data.start_time,
          end_time: data.end_time,
          notes: data.notes,
          condominium_id: profile.condominium_id,
          status: 'pending'
        })
        .select(`
          *,
          common_areas!bookings_area_id_fkey(name, image_emoji)
        `)
        .single()

      if (error) {
        console.error("Erro ao criar agendamento:", error)
        throw error
      }
      
      console.log("Agendamento criado com sucesso:", booking)
      return booking
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["all-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["common-areas"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      toast.success("Agendamento realizado com sucesso! Aguardando aprovação do síndico.")
    },
    onError: (error) => {
      console.error("Erro ao criar agendamento:", error)
      toast.error(error.message || "Erro ao realizar agendamento")
    }
  })
}

export const useUpdateBookingStatus = () => {
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return a mock mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: () => Promise.resolve(),
      isLoading: false,
      isPending: false,
      error: null
    }
  }
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async ({ bookingId, status }: { bookingId: string; status: 'confirmed' | 'rejected' | 'cancelled' }) => {
      if (!profile?.id) throw new Error("Usuário não autenticado")

      console.log("Atualizando status do agendamento:", bookingId, "para:", status)

      const updateData: any = { status }
      
      if (status === 'confirmed' || status === 'rejected') {
        updateData.approved_by = profile.id
        updateData.approved_at = new Date().toISOString()
      }

      const { data, error } = await supabase
        .from("bookings")
        .update(updateData)
        .eq("id", bookingId)
        .select(`
          *,
          common_areas!bookings_area_id_fkey(name, image_emoji),
          profiles!bookings_user_id_fkey(full_name)
        `)
        .single()

      if (error) {
        console.error("Erro ao atualizar agendamento:", error)
        throw error
      }
      
      console.log("Status do agendamento atualizado:", data)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["all-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      
      const statusMessages = {
        confirmed: "Agendamento aprovado com sucesso!",
        rejected: "Agendamento rejeitado",
        cancelled: "Agendamento cancelado"
      }
      
      toast.success(statusMessages[variables.status])
    },
    onError: (error) => {
      console.error("Erro ao atualizar agendamento:", error)
      toast.error("Erro ao atualizar agendamento")
    }
  })
}
